/**
 * Google Ads conversion tracking utility.
 * Fires a conversion event to the configured Google Ads account.
 * The conversion ID is fetched from app_secrets at runtime for admin flexibility.
 */

import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const DEFAULT_CONVERSION_ID = "AW-17920928293/450NCM6IpPAbEKXUruFC";

// Cache the conversion ID to avoid repeated DB calls
let cachedConversionId: string | null = null;
let fetchPromise: Promise<string> | null = null;

/**
 * Fetch the Google Ads conversion ID from app_secrets.
 * Falls back to hardcoded default if not set.
 */
async function getConversionId(): Promise<string> {
  if (cachedConversionId) return cachedConversionId;
  
  if (fetchPromise) return fetchPromise;
  
  fetchPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from("app_secrets")
        .select("value")
        .eq("key", "gtag_conversion_id")
        .maybeSingle();
      
      if (error || !data?.value) {
        cachedConversionId = DEFAULT_CONVERSION_ID;
        return DEFAULT_CONVERSION_ID;
      }
      
      cachedConversionId = data.value;
      return data.value;
    } catch {
      cachedConversionId = DEFAULT_CONVERSION_ID;
      return DEFAULT_CONVERSION_ID;
    }
  })();
  
  return fetchPromise;
}

/**
 * Clear the cached conversion ID (useful after admin updates the setting).
 */
export function clearConversionIdCache() {
  cachedConversionId = null;
  fetchPromise = null;
}

/**
 * Track a Google Ads conversion event.
 * Fetches the conversion ID from app_secrets if not cached.
 */
export async function trackGtagConversion(): Promise<boolean> {
  try {
    if (typeof window === "undefined" || !window.gtag) {
      return false;
    }
    
    const conversionId = await getConversionId();
    
    window.gtag("event", "conversion", {
      send_to: conversionId,
    });
    
    return true;
  } catch {
    // Silent fail - don't break the app for tracking issues
    return false;
  }
}

/**
 * Test conversion tracking - fires event and returns result.
 * Useful for admin testing.
 */
export async function testGtagConversion(): Promise<{ success: boolean; conversionId: string; gtagAvailable: boolean }> {
  const gtagAvailable = typeof window !== "undefined" && typeof window.gtag === "function";
  
  try {
    const conversionId = await getConversionId();
    
    if (!gtagAvailable) {
      return { success: false, conversionId, gtagAvailable: false };
    }
    
    window.gtag!("event", "conversion", {
      send_to: conversionId,
    });
    
    return { success: true, conversionId, gtagAvailable: true };
  } catch {
    return { success: false, conversionId: DEFAULT_CONVERSION_ID, gtagAvailable };
  }
}